import { getMessageExceptionDescription, getMessageExceptionType, setMessageException, LambdaCallback, IWorkflow } from '@augloop/interfaces';
import { Log, LogCategory, CorrelationVector, LoggingAggregator, Operation as LoggingOperation } from '@augloop/logging/dist/src/client/logging';
import { IType, TypeCategory, MessageType, IClientMetadata, ICorrelationVector, ExceptionType, Reduce, Tiling, Ppt, IException } from '@augloop/schemas/dist/src/client/schemas';
import { ICacheDumpRequestResponse, Message, IResponse, ResponseCallback } from '@augloop/session-protocol';
import { IOperation, SchemaObject, ItemPath, AnnotationState } from '@augloop/types-core';

import { IClientRuntime, IHostCallbacks, IRuntimeInitOptions, IBatchConfig, IBatchOptions, ISessionCreationOptions } from './iclientruntime';
import { ILambda, ICustomLambda, LambdaSource, ILocalLambda, IRemoteLambda, IReduceLambda, IReduceWorkflowGroupingKey, IThrottleSettings, IWorkflowRegistrationOptions, ILambdaContext, IBatchedRemoteLambda } from './deprecated-runtime-types';
import * as RemoteLambdaHttp from './remote-lambda-http';
import * as RemoteLambdaWs from './remote-lambda-ws';
import { ReduceTimerCache } from './reduce-timer-cache';
import { ResultCache } from './result-cache';
import { TelemetryLogger } from './telemetry-logger';
import { IClientMessage } from './iclientmessage';
import { WebSocketWorkerFactory, WebSocketWorker } from './websocket/websocket-worker';
import { WebSocketSessionManager } from './websocket/websocket-session-manager';
import { IExecution, ExecutionWithThrottling } from './throttling';
import { BatchManager, IBatch, IBatchContext } from './batch-manager';
import { estimateSize } from './estimate-size';
import { ISession, CacheRequestParameters, AnnotationActivationToken, AnnotationActivationOptions, IAnnotationActivationResult, SessionCloseCallback } from './isession';
import { Session, SessionFactory, ISessionInternal } from './session';
import { Utils } from './utils';
import { SessionManagerFactory, ISessionManager, SessionStats } from './websocket/isession-manager';
import { SessionInitializer } from './websocket/session-initializer';
import { TokenRefreshManager } from './websocket/token-refresh-manager';
import { IModelDownloader } from './local-workflow-execution-manager';

const getVersion = () => {
    /* eslint-disable-next-line node/no-missing-require */
    return require('./version.json').version;
};

enum WorkflowState {
    Restricted, // Workflow will execute sequentially
    Unrestricted, // Workflow can execute in parallel
    Suppressed // Workflow will never run again (in this doc for this session)
}

type WorkflowExecutionCallback = () => void;

export class Workflow {
    public static executionQueuesEnabled = true;
    public static trackingByDocSessionIdEnabled = true;

    public name: string;
    public lambdas: ILambda[];
    public canProduceNullResult: boolean;
    public shouldSendResultsToHost: boolean;
    public triggerOnRefresh: boolean;
    public throttleSettings: IThrottleSettings;
    public docIdExtractor?: (input: any) => string;
    public docSessionIdExtractor?: (input: any) => string;
    public deferReduceUntilIdle: boolean;
    public deferReduceUntilIdleMaxDelayMs: number;
    public useCanaryTextTile: boolean;
    public canaryTextTileEventSubmittedByDocSessionId = new Set<string>();
    public preExecutionPromise?: Promise<void>;
    public stateByDocSessionId = new Map<string, WorkflowState>();
    public executionQueueByDocSessionId = new Map<string, WorkflowExecutionCallback[]>();
    public executionCountByDocSessionId = new Map<string, number>();

    public getOutputSchema(): string {
        return this.lambdas[this.lambdas.length - 1].outputSchema;
    }

    public scheduleExecution(input: unknown, execute: () => void): void {
        let docSessionId;
        if (Workflow.trackingByDocSessionIdEnabled) {
            docSessionId = this.docSessionIdExtractor ? this.docSessionIdExtractor(input) : undefined;
        } else {
            docSessionId = this.docIdExtractor ? this.docIdExtractor(input) : undefined;
        }
        const state = this.getStateForDocSessionId(docSessionId);
        if (state === WorkflowState.Restricted) {
            const queue = this.getExecutionQueueForDocSessionId(docSessionId);
            queue.push(execute);
            if (queue.length === 1) {
                execute();
            }
        } else if (state === WorkflowState.Unrestricted) {
            execute();
        } else if (state === WorkflowState.Suppressed) {
            // Do nothing
        }
    }

    public onStart(docSessionId: string): void {
        this.incrementExecutionCountForDocSessionId(docSessionId);
    }

    public onFinish(docSessionId: string, exceptionType?: ExceptionType): void {
        this.decrementExecutionCountForDocSessionId(docSessionId);
        if (exceptionType === ExceptionType.Authentication) {
            // Suppress workflow if no token or invalid token obtained
            this.stateByDocSessionId.set(docSessionId, WorkflowState.Suppressed);
            this.executionQueueByDocSessionId.delete(docSessionId);
        } else if (this.stateByDocSessionId.get(docSessionId) === WorkflowState.Restricted) {
            const queue = this.getExecutionQueueForDocSessionId(docSessionId);
            queue.shift();
            if (exceptionType === undefined) {
                // We've had the first successful execution, subsequent
                // executions are now unrestricted and can run in parallel
                this.stateByDocSessionId.set(docSessionId, WorkflowState.Unrestricted);
                this.executionQueueByDocSessionId.delete(docSessionId);
                for (const callback of queue) {
                    callback();
                }
            } else if (queue.length > 0) {
                // Keep going one at a time
                const nextCallback = queue[0];
                nextCallback();
            }
        }
    }

    public onRefresh(docSessionId?: string): void {
        // Reset our state map (equivalent to going back to Restricted for all docs)
        // since the auth state may have changed as part of refresh
        this.stateByDocSessionId = new Map();

        if (this.triggerOnRefresh) {
            // Clear the execution queue(s) since all tiles will be resubmitted as part of the refresh
            if (docSessionId) {
                this.executionQueueByDocSessionId.delete(docSessionId);
                this.executionCountByDocSessionId.delete(docSessionId);
            } else {
                this.executionQueueByDocSessionId = new Map();
                this.executionCountByDocSessionId = new Map();
            }
        }

        // If using canary, we'll want to submit it again on refresh
        this.canaryTextTileEventSubmittedByDocSessionId.delete(docSessionId);
    }

    private getStateForDocSessionId(docSessionId: string): WorkflowState {
        let state = this.stateByDocSessionId.get(docSessionId);
        if (state === undefined) {
            state = Workflow.executionQueuesEnabled ? WorkflowState.Restricted : WorkflowState.Unrestricted;
            this.stateByDocSessionId.set(docSessionId, state);
        }
        return state;
    }

    private getExecutionQueueForDocSessionId(docSessionId: string): WorkflowExecutionCallback[] {
        let queue = this.executionQueueByDocSessionId.get(docSessionId);
        if (queue === undefined) {
            queue = [];
            this.executionQueueByDocSessionId.set(docSessionId, queue);
        }
        return queue;
    }

    private incrementExecutionCountForDocSessionId(docSessionId: string) {
        const count = this.executionCountByDocSessionId.get(docSessionId) || 0;
        this.executionCountByDocSessionId.set(docSessionId, count + 1);
    }

    private decrementExecutionCountForDocSessionId(docSessionId: string) {
        const count = this.executionCountByDocSessionId.get(docSessionId) || 0;
        if (count <= 1) {
            this.executionCountByDocSessionId.delete(docSessionId);
        } else {
            this.executionCountByDocSessionId.set(docSessionId, count - 1);
        }
    }
}

interface IBatchedRemoteLambdaContext extends IBatchContext {
    remoteLambda: IBatchedRemoteLambda;
    lambdaContext: ILambdaContext;
}

export const getTypeFromSchema: (schema: string) => IType = (schema) => {
    return {
        category: TypeCategory.Schema,
        schema: {
            name: schema
        }
    };
};

const textTileSchema = 'Tiling.TextTileEvent';
const slideTileSchema = 'ClpSlideTile';

const isTileDeletedEvent = (inputSchema: string, input: any): boolean => {
    return ((inputSchema === textTileSchema && input && input.type == Tiling.TextTileEventType.Delete) || (inputSchema === slideTileSchema && input && input.eventType == Ppt.SlideTileEventType.Delete));
};

const isTextTileRefreshEvent = (inputSchema: string, input: any): boolean => {
    return inputSchema === textTileSchema && input && input.type == Tiling.TextTileEventType.Refresh;
};

const isSlideTileRefreshEvent = (inputSchema: string, input: any): boolean => {
    return inputSchema === slideTileSchema && input && input.eventType == Ppt.SlideTileEventType.Refresh;
};

const isTileRefreshEvent = (inputSchema: string, input: any): boolean => {
    return isTextTileRefreshEvent(inputSchema, input) || isSlideTileRefreshEvent(inputSchema, input);
};

const getTileRefreshSeq = (inputSchema: string, input: any): number => {
    if (isTextTileRefreshEvent(inputSchema, input)) {
        return input.tile?.metadata?.seqnoCLPRefresh;
    }
    if (isSlideTileRefreshEvent(inputSchema, input)) {
        return -1; // NYI
    }
    return -1;
};

const getTileDocSessionId = (inputSchema: string, input: any): string => {
    if (inputSchema === textTileSchema) {
        return input.tile?.metadata?.docSessionId;
    }
    if (inputSchema === slideTileSchema) {
        return input.docId; // Populated by PPT like a docSessionId (has a new value when re-opening the same doc)
    }
    return undefined;
};

const createCanaryTextTileEvent = (template: any): any => {
    return {
        type: template.type,
        tile: {
            metadata: {
                docSessionId: template.tile.metadata.docSessionId,
                docId: template.tile.metadata.docId,
                seqnoCLPRefresh: template.tile.metadata.seqnoCLPRefresh,
                tileId: 'A56B3127DBDFD0D6'
            },
            elements: [{
                text: 'B91153AE828B4E48'
            }]
        }
    };
};

export type ExecuteRemoteLambdaCallback = (
    url: string,
    message: IClientMessage,
    inputSchema: IType,
    outputSchema: IType,
    correlationVector: ICorrelationVector,
    clientMetadata: IClientMetadata,
    hostCallbacks: IHostCallbacks,
    context: ILambdaContext,
    done: LambdaCallback<any>
) => void;

export class ClientRuntime implements IClientRuntime {
    private readonly inputSchemasToWorkflows: Map<string, Workflow[]> = new Map();
    private readonly inputSchemasToReduceWorkflows: Map<string, Workflow[]> = new Map();
    private readonly reduceWorkflowsGroupingKeyExtractors: Map<string, (input: any, inputSchema: string) => IReduceWorkflowGroupingKey> = new Map();
    private readonly defaultWorkflowOptions: IWorkflowRegistrationOptions = { canProduceNullResult: false, shouldSendResultsToHost: true, enabledByDefault: false, throttleSettings: { shouldBeThrottled: false }, triggerOnRefresh: false }
    private serviceUrl: string;
    private serviceProtocol: string;
    private clientMetadata: IClientMetadata;
    private hostCallbacks: IHostCallbacks;
    private executeRemoteLambda: ExecuteRemoteLambdaCallback;
    private batchedRemoteLambdaManager: BatchManager;
    private clearRemoteLambdaTokenCache: () => void;
    private readonly resultCache: ResultCache = new ResultCache();
    private readonly reduceTimerCache: ReduceTimerCache = new ReduceTimerCache();
    private lookupTableCache: Map<string, any> = new Map();
    private workerFactory: WebSocketWorkerFactory;
    private sessionManagerFactory: SessionManagerFactory;
    private sessionFactory: SessionFactory;
    private readonly sessionsByDocSessionId = new Map<string, ISessionInternal>();
    private throttling: IExecution = new ExecutionWithThrottling();
    private batchOptions: IBatchOptions;
    private lastTileRefreshSeq: number = -1;
    private lastTileRefreshSeqByDocSessionId = new Map<string, number>();
    private dropOnlySequencedSyncsOnReseedEnabled = true; // ChangeGate, can be removed once sufficiently baked
    private tileRefreshByDocSessionIdEnabled = true; // ChangeGate, can be removed once sufficiently baked
    private modelDownloader: IModelDownloader;

    public constructor(workerFactory?: WebSocketWorkerFactory, sessionManagerFactory?: SessionManagerFactory, sessionFactory?: SessionFactory) {
        this.workerFactory = workerFactory || (() => new WebSocketWorker());
        this.sessionManagerFactory = sessionManagerFactory || this.createDefaultSessionManagerFactory();
        this.sessionFactory = sessionFactory || ((config) => new Session(config));
    }

    public init(serviceUrl: string, clientMetadata: IClientMetadata, hostCallbacks: IHostCallbacks, options: IRuntimeInitOptions): Promise<void> {
        const op = new LoggingOperation({
            operationName: 'InitRuntime',
            resourceId: serviceUrl
        });
        op.start();

        this.modelDownloader = options.modelDownloader;

        this.clientMetadata = clientMetadata;
        if (this.clientMetadata) {
            this.clientMetadata.runtimeVersion = getVersion();
        }

        this.hostCallbacks = hostCallbacks;

        Log.addLogger(new TelemetryLogger(this.hostCallbacks));

        let telemetryAggregationIntervalSec = options.telemetryAggregationIntervalSec;
        if (telemetryAggregationIntervalSec === undefined) {
            // Aggregate by default if the interval is not explicitly specified
            telemetryAggregationIntervalSec = 30;
        }
        if (telemetryAggregationIntervalSec > 0) {
            const createOperationLoggingAggregator = (eventName: string, operationNameField: string, operationNamesToAggregateOnSuccess: string[]) => new LoggingAggregator(
                eventName,
                (obj: any) => obj.success && operationNamesToAggregateOnSuccess.indexOf(obj[operationNameField]) >= 0,
                // Dimensions
                [
                    operationNameField,
                    'resourceId',
                    'success',
                    'resultSignature',
                    'dimension0',
                    'dimension1',
                    'dimension2',
                    'dimension3'
                ],
                // Measures
                ['durationMs'],
                telemetryAggregationIntervalSec);
            Log.addAggregator(createOperationLoggingAggregator('Operation', 'operationName', ['ExecuteLambda', 'ExecuteWorkflow', 'ExecuteBatch', 'ProcessResponse']));
            Log.addAggregator(createOperationLoggingAggregator('SessionHealth', 'sessionHealthEventName', ['ProcessMessage', 'SendMessage']));
        }

        this.batchOptions = options.batchOptions;

        const promises: Promise<void>[] = [];
        promises.push(
            this.isFeatureEnabled('DisableWebSocket')
                .then((isWebSocketDisabled) => {
                    // The isFeatureEnabled check above functions effectively as a ChangeGate, so invert the
                    // rest of our logic to use what would have been the return value of a ChangeGate call.
                    // NOTE: unit tests and automation still rely on DisableWebSocket; do not remove this
                    // until that dependency no longer exists.
                    const isWebSocketEnabled = !isWebSocketDisabled;

                    if (!isWebSocketEnabled) {
                        return false;
                    }

                    const appsUsingALv1 = [
                        'Outlook Mac',
                        'Outlook Win32',
                        'PowerPoint Mac',
                        'PowerPoint Web',
                        'PowerPoint Win32',
                        'Word Mac',
                        'Word Win32'
                    ];

                    if (appsUsingALv1.indexOf(`${clientMetadata.appName} ${clientMetadata.appPlatform}`) >= 0) {
                        // Real-world network infrastructure sometimes doesn't support WebSocket even if the client does, so before
                        // we commit to using WebSocket for ALv1 for this session, ensure that we can establish a healthy connection first.
                        return RemoteLambdaWs.testWebSocketConnection(Utils.convertServiceUrlToWebSocket(serviceUrl), this.clientMetadata);
                    } else {
                        // We can skip the connection test if the app won't be using ALv1.
                        // TODO: we also need to support an HTTP fallback for ALv2 sessions, but that will be implemented at the session
                        // level, not here; see User Story 3240069: HTTPS fallback when WebSocket isn't supported
                        return true;
                    }
                })
                .then((isWebSocketEnabled) => {
                    if (serviceUrl) {
                        this.serviceUrl = isWebSocketEnabled ? Utils.convertServiceUrlToWebSocket(serviceUrl) : serviceUrl;
                        this.executeRemoteLambda = isWebSocketEnabled ? RemoteLambdaWs.executeRemoteLambda : RemoteLambdaHttp.executeRemoteLambda;
                        this.clearRemoteLambdaTokenCache = isWebSocketEnabled ? RemoteLambdaWs.clearTokenCache : RemoteLambdaHttp.clearTokenCache;

                        this.serviceProtocol = this.serviceUrl.split(':')[0].toLowerCase();
                    }
                }));

        promises.push(this.isFeatureEnabled('WorkflowQueuesDisabled').then((isDisabled: boolean) => {
            Workflow.executionQueuesEnabled = !isDisabled;
        }));

        promises.push(this.isFeatureEnabled('WorkflowTrackingByDocSessionIdDisabled').then((isDisabled: boolean) => {
            Workflow.trackingByDocSessionIdEnabled = !isDisabled;
        }));

        promises.push(this.isFeatureEnabled('DropOnlySequencedSyncsOnReseedDisabled').then((isDisabled: boolean) => {
            this.dropOnlySequencedSyncsOnReseedEnabled = !isDisabled;
        }));

        promises.push(this.isFeatureEnabled('TileRefreshByDocSessionIdDisabled').then((isDisabled: boolean) => {
            this.tileRefreshByDocSessionIdEnabled = !isDisabled;
        }));

        return Promise.all(promises)
            .then(() => {
                if (this.serviceUrl && options.initSession) {
                    this.createSession();
                }

                this.logOperation(op, true);
            })
            .catch((error) => {
                this.logOperation(op, false, 'Error', error ? error.message : '(no error)');
            });
    }

    public getServiceProtocol(): string {
        return this.serviceProtocol;
    }

    public registerSchemas(_proto: string, _schemas: string[]): Promise<void> {
        return Promise.resolve();
    }

    public registerSimpleLocalWorkflow(name: string, lambda: ICustomLambda, options: IWorkflowRegistrationOptions = this.defaultWorkflowOptions): Promise<void> {
        lambda.source = lambda.source || LambdaSource.Custom; // This can be either Local or Custom; default to Custom
        return this.registerHardcodedWorkflow(name, [lambda], this.inputSchemasToWorkflows, options);
    }

    public registerSimpleRemoteWorkflow(name: string, lambdaBefore: ICustomLambda, lambdaRemote: IRemoteLambda, lambdaAfter: ICustomLambda, options: IWorkflowRegistrationOptions = this.defaultWorkflowOptions): Promise<void> {
        const lambdas: ILambda[] = [];
        if (lambdaBefore) {
            lambdaBefore.source = LambdaSource.Custom;
            lambdas.push(lambdaBefore);
        }
        lambdaRemote.source = (lambdaRemote as ICustomLambda).func ? LambdaSource.Custom : LambdaSource.Remote; // If caller actually passed in a Custom lambda, treat it as such
        lambdas.push(lambdaRemote);
        if (lambdaAfter) {
            lambdaAfter.source = LambdaSource.Custom;
            lambdas.push(lambdaAfter);
        }
        return this.registerHardcodedWorkflow(name, lambdas, this.inputSchemasToWorkflows, options);
    }

    public registerMultipleLambdasWorkflow(name: string, lambdas: ILambda[], options: IWorkflowRegistrationOptions = this.defaultWorkflowOptions): Promise<void> {
        return this.registerHardcodedWorkflow(name, lambdas, this.inputSchemasToWorkflows, options);
    }

    public registerReduceWorkflow(name: string, lambda: IReduceLambda, groupingKeyExtractor: (input: any, inputSchema: string) => IReduceWorkflowGroupingKey, bucketSize: number = 10, options: IWorkflowRegistrationOptions = this.defaultWorkflowOptions): Promise<void> {
        this.reduceWorkflowsGroupingKeyExtractors.set(lambda.inputSchema, groupingKeyExtractor);
        this.resultCache.setBucketSize(lambda.inputSchema, bucketSize);

        lambda.source = lambda.source || LambdaSource.Custom; // Default to custom
        return this.registerHardcodedWorkflow(name, [lambda], this.inputSchemasToReduceWorkflows, options);
    }

    public registerMultipleLambdasReduceWorkflow(name: string, lambdas: ILambda[], groupingKeyExtractor: (input: any, inputSchema: string) => IReduceWorkflowGroupingKey, bucketSize: number = 10, options: IWorkflowRegistrationOptions = this.defaultWorkflowOptions): Promise<void> {
        this.reduceWorkflowsGroupingKeyExtractors.set(lambdas[0].inputSchema, groupingKeyExtractor);
        this.resultCache.setBucketSize(lambdas[0].inputSchema, bucketSize);
        return this.registerHardcodedWorkflow(name, lambdas, this.inputSchemasToReduceWorkflows, options);
    }

    public registerLocalWorkflow(workflow: IWorkflow): void {
        this.getSession().registerLocalWorkflow(workflow);
    }

    public activateAnnotation(annotationType: string, options?: AnnotationActivationOptions): Promise<IAnnotationActivationResult> {
        return this.getSession().activateAnnotation(annotationType, options);
    }

    public updateAnnotationConfig(token: AnnotationActivationToken, config: SchemaObject): void {
        this.getSession().updateAnnotationConfig(token, config);
    }

    public releaseAnnotation(token: AnnotationActivationToken): Promise<boolean> {
        return this.getSession().releaseAnnotation(token);
    }

    public setAnnotationState(parentPath: ItemPath, id: string, value: AnnotationState): void {
        this.getSession().setAnnotationState(parentPath, id, value);
    }

    public submit(inputSchema: string, input: unknown, cv?: string): void {
        this.submitToWorkflows(inputSchema, input, cv, this.inputSchemasToWorkflows);
    }

    public submitOperation(operation: IOperation, cv?: string): void {
        this.getSession().submitOperation(operation, cv);
    }

    public submitOperations(operations: IOperation[], cv?: string): void {
        this.getSession().submitOperations(operations, cv);
    }

    public submitCustomMessage(message: Message): Promise<IResponse> {
        return this.getSession().submitCustomMessage(message);
    }

    public submitSeedOperations(operations: IOperation[], cv?: string): void {
        this.getSession().submitSeedOperations(operations, cv);
    }

    public submitSeedGroupOperations(operations: IOperation[], groupComplete?: boolean, cv?: string): void {
        this.getSession().submitSeedGroupOperations(operations, groupComplete, cv);
    }

    public flushTelemetry(stop: boolean): void {
        Log.flushAggregators(stop);
    }

    public requestCacheDump(cacheRequestParameters?: CacheRequestParameters): Promise<ICacheDumpRequestResponse> {
        return this.getSession().requestCacheDump(cacheRequestParameters);
    }

    public forceReconnectSession(): Promise<void> {
        return this.getSession().forceReconnect();
    }

    public setSessionCloseCallback(callback: SessionCloseCallback): void {
        this.getSession().setSessionCloseCallback(callback);
    }

    public closeSession(): void {
        this.getSession().close();
    }

    public createSession(options?: ISessionCreationOptions): Promise<ISession> {
        const docSessionId = options ? options.docSessionId : undefined;
        const existingSession: ISessionInternal = this.sessionsByDocSessionId.get(docSessionId);
        if (existingSession && existingSession.isClosed === false) {
            throw new Error('docSessionId already exists');
        }

        // Every session gets its own customized copy of the metadata
        const clientMetadata: IClientMetadata = { ...this.clientMetadata };
        clientMetadata.docSessionId = docSessionId;
        if (options && options.flights) {
            clientMetadata.flights = clientMetadata.flights
                ? clientMetadata.flights + ';' + options.flights
                : options.flights;
        }

        const session: ISessionInternal = this.sessionFactory({
            hostCallbacks: this.hostCallbacks,
            sessionManager: this.sessionManagerFactory(clientMetadata),
            batchOptions: this.batchOptions,
            extensionConfigs: options?.extensionConfigs || [],
            clientMetadata: clientMetadata,
            modelDownloader: this.modelDownloader
        });
        this.sessionsByDocSessionId.set(docSessionId, session);

        if (options && options.onSessionConnect) {
            session.setConnectCallback(options.onSessionConnect);
        }

        if (options && options.onSessionDisconnect) {
            session.setDisconnectCallback(options.onSessionDisconnect);
        }

        if (options && options.onSessionReconnect) {
            session.setReconnectCallback(options.onSessionReconnect);
        }

        if (options && options.onSessionClose) {
            session.setSessionCloseCallback(options.onSessionClose);
        }

        return session.initialize ? session.initialize() : Promise.resolve(session);
    }

    private getSession(docSessionId?: string): ISessionInternal {
        return this.sessionsByDocSessionId.get(docSessionId);
    }

    private createDefaultSessionManagerFactory(): SessionManagerFactory {
        return (clientMetadata: IClientMetadata): ISessionManager => {
            const tokenRefreshManager: TokenRefreshManager = new TokenRefreshManager();
            const sessionStats = new SessionStats();
            let sendMessage: (message: Message, onResponse?: ResponseCallback) => void = () => { Log.error(0x222c318f /* tag_8ldgp */, LogCategory.CoreDefault, 'Unexpectedly not set sendMessage'); };
            const sessionInitializer = new SessionInitializer(
                () => sessionStats,
                clientMetadata,
                tokenRefreshManager,
                (message, onResponse) => { sendMessage(message, onResponse); },
                {
                    getAuthToken: this.getAuthToken.bind(this),
                    overrideSessionInitMessage: this.hostCallbacks.overrideSessionInitMessage
                });

            const sessionManager = new WebSocketSessionManager(
                Utils.convertServiceUrlToWebSocket(this.serviceUrl),
                sessionStats,
                this.workerFactory,
                sessionInitializer,
                clientMetadata,
                this.dropOnlySequencedSyncsOnReseedEnabled);
            sendMessage = sessionManager.sendMessage.bind(sessionManager);

            sessionManager.on('disconnect', () => tokenRefreshManager.clearTimeouts());
            sessionManager.on('connect', (isSeedingRequired, sessionUrl, origin, authToken) => {
                if (isSeedingRequired && this.hostCallbacks.setSessionData) {
                    this.hostCallbacks.setSessionData(sessionUrl, origin, authToken);
                }
            });

            return sessionManager;
        };
    }

    private getAuthToken(): Promise<string> {
        return this.hostCallbacks.requestAuthToken({ Tickets: [] })
            .then((tokenResponse) => {
                if (!tokenResponse) {
                    return undefined;
                }

                // In case we got the token from the host, extract its value
                return tokenResponse.Token;
            })
            .catch(() => {
                return undefined;
            });
    }

    private getGroupingKey(inputSchema: string, input: any, resultSchema: string): IReduceWorkflowGroupingKey {
        const groupingKeyExtractor = this.reduceWorkflowsGroupingKeyExtractors.get(resultSchema);
        if (groupingKeyExtractor == undefined) {
            throw new Error('Reduce workflow does not have a grouping extractor');
        }
        return groupingKeyExtractor(input, inputSchema);
    }

    private submitToWorkflows(
        inputSchema: string,
        input: any,
        cv?: string,
        inputSchemasToWorkflows: Map<string, Workflow[]> = this.inputSchemasToWorkflows
    ): void {
        if (!this.hostCallbacks) {
            return;
        }

        let cvParent;
        if (!cv) {
            cvParent = new CorrelationVector();
        } else {
            cvParent = CorrelationVector.fromString(cv);
        }

        this.handleTileRefresh(inputSchema, input, cvParent);

        const workflows = inputSchemasToWorkflows.get(inputSchema) || [];
        for (const workflow of workflows) {
            if (isTileRefreshEvent(inputSchema, input) && !workflow.triggerOnRefresh) {
                continue;
            }

            if (workflow.useCanaryTextTile && inputSchema === textTileSchema && !workflow.canaryTextTileEventSubmittedByDocSessionId.has(getTileDocSessionId(inputSchema, input))) {
                // A canary text tile is a fixed-content tile that is submitted before the actual tile content as a way
                // of ensuring that, under normal circumstances, the actual tile content has an opportunity to be batched
                // together with nearby tiles (when the workflow is in unrestricted mode), which improves the quality of
                // some workflow output (e.g. CLP).
                this.submitToWorkflow(textTileSchema, createCanaryTextTileEvent(input), cvParent, workflow, inputSchemasToWorkflows);
                workflow.canaryTextTileEventSubmittedByDocSessionId.add(getTileDocSessionId(inputSchema, input));
            }

            this.submitToWorkflow(inputSchema, input, cvParent, workflow,  inputSchemasToWorkflows);
        }
    }

    private submitToWorkflow(inputSchema: string, input: any, cvParent: any, workflow: Workflow, inputSchemasToWorkflows: Map<string, Workflow[]>) {
        const message: IClientMessage = {
            messageType: MessageType.Input,
            correlationVector: cvParent.newChild(),
            payload: input,
            payloadSchema: getTypeFromSchema(workflow.lambdas[0].inputSchema),
            clientMetadata: this.clientMetadata
        };

        const op = new LoggingOperation({
            cv: message.correlationVector.toString(),
            operationName: 'ExecuteWorkflow',
            resourceId: workflow.name
        });
        op.start();

        workflow.scheduleExecution(input, () => {
            try {
                if (!message.payload || !message.payload.tile || !message.payload.tile.metadata || !message.payload.tile.metadata.tileId) {
                    this.executeWorkflow(op, message, input, workflow, inputSchema, inputSchemasToWorkflows);
                } else {
                    // Use throttled execution for tile inputs
                    const tileId = message.payload.tile.metadata.tileId;
                    this.throttling.execute(tileId, workflow, () => this.executeWorkflow(op, message, input, workflow, inputSchema, inputSchemasToWorkflows));
                }
            } catch (error) {
                op.success = false;
                op.resultSignature = 'ExecuteException';
                op.resultDescription = error ? error.message : '(no error)';
                Log.error(0x222c3190 /* tag_8ldgq */, LogCategory.CoreDefault, op.stop());
            }
        });
    }

    private handleTileRefresh(inputSchema: string, input: any, cv: CorrelationVector) {
        if (!isTileRefreshEvent(inputSchema, input)) {
            return;
        }

        const docSessionId = this.tileRefreshByDocSessionIdEnabled ? getTileDocSessionId(inputSchema, input) : undefined;

        // Nothing to do if we've already seen a tile associated with this seq
        const seq = getTileRefreshSeq(inputSchema, input);
        if (this.tileRefreshByDocSessionIdEnabled) {
            const lastTileRefreshSeq = this.lastTileRefreshSeqByDocSessionId.get(docSessionId);
            if (lastTileRefreshSeq >= 0 && seq <= lastTileRefreshSeq) {
                return;
            }
        } else {
            if (seq <= this.lastTileRefreshSeq) {
                return;
            }
        }

        const op = new LoggingOperation({
            cv: cv.toString(),
            operationName: 'TileRefresh',
            resultDescription: '',
            success: true
        });
        op.start();

        // We require tile refreshes to be associated with a docSessionId
        if (this.tileRefreshByDocSessionIdEnabled && !docSessionId) {
            op.success = false;
            op.resultDescription = 'NoDocSessionId';
            Log.info(0x222db401 /* tag_8l1qb */, LogCategory.CoreDefault, op.stop());
            return;
        }

        // Clear caches for tile-refresh-aware reduce workflows
        this.inputSchemasToReduceWorkflows.forEach((workflows, reduceInputSchema) => {
            if (workflows.some(x => x.triggerOnRefresh)) {
                if (this.tileRefreshByDocSessionIdEnabled) {
                    // Clear the result cache for this schema (at potentially both doc and tile level)
                    this.resultCache.clear(reduceInputSchema, { primary: docSessionId });
                    this.resultCache.clear(this.getForcedDocLevelSchema(reduceInputSchema), { primary: docSessionId });

                    // Cancel any pending reduces that might emit stale results into the cache
                    this.reduceTimerCache.clear(reduceInputSchema, docSessionId);
                } else {
                    // Clear the result caches for this schema (at potentially both doc and tile level)
                    this.resultCache.clear(reduceInputSchema);
                    this.resultCache.clear(this.getForcedDocLevelSchema(reduceInputSchema));

                    // Cancel any pending reduces that might emit stale results into the cache
                    this.reduceTimerCache.clear(reduceInputSchema);
                }

                if (workflows.some(x => !x.triggerOnRefresh)) {
                    // Reaching this would be weird, which is why we're just using a short
                    // and cryptic string here to avoid bloating the bundle for a condition
                    // we'll likely never see (but we still want to know if it ever happens).
                    // This can be removed once we don't expect new ALv1 workflows.
                    op.resultDescription += 'X_'; // Unexpected
                }

                // Record all reduce input schemas that we cleared
                op.resultDescription += reduceInputSchema + ' ';
            }
        });

        // Force auth tokens to be refreshed (no simple way to do this just for tile-refresh-aware
        // workflows, so we do it across the board; should have no functional impact other than
        // causing remote lambdas to fetch new tokens next time they run)
        this.clearRemoteLambdaTokenCache();

        // Reset all workflow execution states (since we cleared all auth tokens)
        const allWorkflows: Workflow[] = [];
        this.inputSchemasToWorkflows.forEach(workflows => allWorkflows.push(...workflows));
        this.inputSchemasToReduceWorkflows.forEach(workflows => allWorkflows.push(...workflows));
        for (const workflow of allWorkflows) {
            if (this.tileRefreshByDocSessionIdEnabled) {
                workflow.onRefresh(docSessionId);
            } else {
                workflow.onRefresh();
            }
        }

        if (this.tileRefreshByDocSessionIdEnabled) {
            this.lastTileRefreshSeqByDocSessionId.set(docSessionId, seq);
        } else {
            this.lastTileRefreshSeq = seq;
        }

        Log.info(0x222c3191 /* tag_8ldgr */, LogCategory.CoreDefault, op.stop());
    }

    /**
     * Schedules workflow lambdas and finish step after all lambdas have completed
     */
    private executeWorkflow(op: LoggingOperation, message: IClientMessage, input: any, workflow: Workflow, inputSchema: string, inputSchemasToWorkflows: Map<string, Workflow[]>): void {
        const docId = workflow.docIdExtractor ? workflow.docIdExtractor(message.payload) : undefined;
        const docSessionId = Workflow.trackingByDocSessionIdEnabled ? workflow.docSessionIdExtractor(input) : docId;
        const tileRefreshSeqForThisExecution = this.tileRefreshByDocSessionIdEnabled ? this.lastTileRefreshSeqByDocSessionId.get(docSessionId) : this.lastTileRefreshSeq;
        workflow.onStart(docSessionId);

        const finish = (message: IClientMessage): void => {
            // If this is a tile-refresh-aware workflow and a tile refresh occurred
            // since we began workflow execution, we'll want to ignore the results
            // (since we now only care about the new executions triggered by the refresh)
            const lastTileRefreshSeq = this.tileRefreshByDocSessionIdEnabled ? this.lastTileRefreshSeqByDocSessionId.get(docSessionId) : this.lastTileRefreshSeq;
            if (workflow.triggerOnRefresh && lastTileRefreshSeq !== tileRefreshSeqForThisExecution) {
                op.success = true; // Not a failure
                op.resultSignature = 'TileRefreshIgnore';
                Log.info(0x222c3192 /* tag_8ldgs */, LogCategory.CoreDefault, op.stop());
                return;
            }

            const isNoOutputMessage: boolean = message.messageType == MessageType.Exception && message.payload.exceptionType == ExceptionType.NoOutput;
            const shouldProduceNullResult: boolean = isNoOutputMessage && workflow.canProduceNullResult;

            // Get workflow output information
            const outputSchema: string = shouldProduceNullResult ? workflow.lambdas[workflow.lambdas.length - 1].outputSchema : message.payloadSchema.schema.name;
            const output: any = shouldProduceNullResult ? null : message.payload;

            if (message.messageType === MessageType.Input || shouldProduceNullResult) {
                if (workflow.shouldSendResultsToHost) {
                    if (inputSchemasToWorkflows === this.inputSchemasToReduceWorkflows) {
                        this.hostCallbacks.onResult('Reduce.Input', input, outputSchema, output);
                    } else {
                        this.hostCallbacks.onResult(inputSchema, input, outputSchema, output);
                    }
                }

                op.success = true;
                op.resultSignature = isNoOutputMessage ? 'NoOutput' : 'ValidOutput';
            } else if (isNoOutputMessage) {
                op.success = true;
                op.resultSignature = 'NoOutput';
            } else {
                op.success = false;
                op.resultSignature = 'Exception';
                op.resultDescription = getMessageExceptionDescription(message);
            }

            if (inputSchemasToWorkflows !== this.inputSchemasToReduceWorkflows) {
                this.updateResultCacheForReduceWorkflows(inputSchema, input, outputSchema, output, message.correlationVector.toString());
            }

            if (!message.moreResults) {
                op.stop();
                Log.info(0x222c3193 /* tag_8ldgt */, LogCategory.CoreDefault, op);
                workflow.onFinish(docSessionId, (message.payload && message.payload.exceptionType) ? getMessageExceptionType(message) : undefined);
            }
        };

        workflow.preExecutionPromise.then(() => {
            const lambdaContext: ILambdaContext = {
                lookupTable: this.lookupTableCache.get(workflow.name),
                docId
            };

            // In reverse workflow order, build up the done() chain
            let nextStep = finish;
            for (let i = workflow.lambdas.length - 1; i >= 0; i--) {
                nextStep = this.createLambdaExecutionStep(workflow.name, workflow.lambdas[i], lambdaContext, nextStep, finish);
            }

            // Start the workflow execution
            nextStep(message);
        });
    }

    private getForcedDocLevelSchema(schema: string) {
        // There is special logic here to support the results of a single outputSchema to be stored
        // in the result cache both at the doc level AND at the tile level. Because the result cache
        // normally can only store a given outputSchema at either of those levels, we get around this
        // by synthesizing a dummy outputSchema name when inputSchema is 'docId'. Tile level results
        // for outputSchema, say, Baz are stored at 'Baz' and doc level results are stored at '~Baz'.
        // We recombine them in the reduce timer callback.
        return `~${schema}`;
    }

    private updateResultCacheForReduceWorkflows(inputSchema: string, input: any, outputSchema: string, output: any, cv?: string): void {
        const reduceWorkflows = this.inputSchemasToReduceWorkflows.get(outputSchema);
        if (reduceWorkflows) {
            let groupingKey: IReduceWorkflowGroupingKey;
            if (inputSchema === 'docId') {
                // Set the output as a doc-level result using the groupingKeyExtractor defined for the reduce
                // workflow with the modified outputSchema name so it doesn't replace the tile-level results...
                groupingKey = this.getGroupingKey(inputSchema, input, outputSchema);
                this.resultCache.setResult(this.getForcedDocLevelSchema(outputSchema), groupingKey, output);
                // ...then set the secondary key to an arbitrary value to force the result cache to return
                // tile-level results in the reduce timer callback (if this happens to be the only or last
                // call to this method for this doc). This rather hackily relies on the fact that getResults
                // only checks for the existence of the secondary key and doesn't care about its value.
                groupingKey.secondary = '*';
            } else {
                groupingKey = this.getGroupingKey(inputSchema, input, outputSchema);

                // Remove results from cache if this is a TileDeleted event
                if (isTileDeletedEvent(inputSchema, input)
                    && groupingKey.primary != undefined
                    && groupingKey.secondary != undefined) {
                    this.resultCache.clear(undefined/*resultSchema*/, groupingKey);
                } else {
                    this.resultCache.setResult(outputSchema, groupingKey, output);
                }
            }

            // If any workflow has requested it, defer reduce execution while any workflow that is upstream of the
            // reduce workflow(s) still has pending executions for this doc
            const anyWorkflowsDeferReduceUntilIdle = reduceWorkflows.some(x => x.deferReduceUntilIdle);
            const upstreamWorkflows: Workflow[] = [];
            this.inputSchemasToWorkflows.forEach(workflows => upstreamWorkflows.push(...workflows.filter(x => x.getOutputSchema() === outputSchema)));
            const shouldDefer = () => {
                return anyWorkflowsDeferReduceUntilIdle && upstreamWorkflows.some(x => x.executionCountByDocSessionId.get(groupingKey.primary) > 0);
            };
            // But only up to the max deferral time specified by any of the workflows
            const maxDeferMs = reduceWorkflows.map(x => x.deferReduceUntilIdleMaxDelayMs || 0).reduce((prev, curr) => Math.max(prev, curr));

            this.reduceTimerCache.setReduceTimer(outputSchema, groupingKey.primary, () => {
                const results: any[] = this.resultCache.getResults(outputSchema, groupingKey);

                // Recombine tile-level and doc-level results (if any) for the same outputSchema name
                results.push(...this.resultCache.getResults(this.getForcedDocLevelSchema(outputSchema), { primary: groupingKey.primary }));

                if (Array.isArray(results)) {
                    const reduceInput: Reduce.IInput = { id: groupingKey.primary, values: results, isReduceInput: true } as Reduce.IInput;
                    this.submitToWorkflows(outputSchema, reduceInput, cv, this.inputSchemasToReduceWorkflows);
                } else {
                    Log.error(0x222c3194 /* tag_8ldgu */, LogCategory.CoreDefault, `Cached results have unexpected type "${typeof results}"`);
                }
            }, 500/*reduceTimeoutMs*/, shouldDefer, maxDeferMs);
        }
    }

    private isFeatureEnabled(featureName: string, defaultValue: boolean = false, scope: string = 'None'): Promise<boolean> {
        return this.hostCallbacks && this.hostCallbacks.isFeatureEnabled
            ? this.hostCallbacks.isFeatureEnabled('Microsoft.Office.AugLoop.' + featureName, scope)
                .catch(() => Promise.resolve(defaultValue))
            : Promise.resolve(defaultValue);
    }

    private areLicenseFeaturesEnabled(licenseFeatures: number[]): Promise<boolean> {
        return this.hostCallbacks && this.hostCallbacks.areLicenseFeaturesEnabled
            ? this.hostCallbacks.areLicenseFeaturesEnabled(licenseFeatures)
                .catch(() => Promise.resolve(false))
            : Promise.resolve(false);
    }

    private isWorkflowEnabled(name: string, lambdas: ILambda[], licenseFeatures: number[], enabledByDefault: boolean, op: LoggingOperation): Promise<boolean> {
        const isRemoteWorkflow = lambdas.some((lambda) => lambda.source === LambdaSource.Remote);
        if (isRemoteWorkflow) {
            if (!this.serviceUrl) {
                op.resultDescription = 'Disabled because no service URL available for remote workflow';
                return Promise.resolve(false);
            }
            // check if we have any non-allow-listed remote lambdas in private mode
            if (this.clientMetadata && this.clientMetadata.privateMode) {
                if (lambdas.some((lambda) => lambda.source === LambdaSource.Remote && !lambda.canRunInPrivateMode)) {
                    op.resultDescription = 'Disabled because we are running in private mode';
                    return Promise.resolve(false);
                }
            }
        }

        if (!this.clientMetadata || (this.clientMetadata.appPlatform !== 'Mac' && this.clientMetadata.appPlatform !== 'Win32')) {
            // Only Win32/Mac currently need an explicit enabled by default
            enabledByDefault = true;
            op.resultDescription = 'Enabled by default due to platform name';
        }

        let isWorkflowEnabled: Promise<Boolean>;
        if (enabledByDefault) {
            isWorkflowEnabled = Promise.resolve(true);
        } else {
            isWorkflowEnabled = this.isFeatureEnabled('WorkflowEnabled.' + name);
        }

        return isWorkflowEnabled.then((isEnabled: boolean) => {
            if (isEnabled) {
                return this.isFeatureEnabled('WorkflowDisabled.' + name).then((isDisabled: boolean) => {
                    if (isDisabled) {
                        op.resultDescription = 'Disabled because explicitly disabled';
                        return false;
                    }

                    if (licenseFeatures) {
                        return this.areLicenseFeaturesEnabled(licenseFeatures).then((isEnabled: boolean) => {
                            if (!isEnabled) {
                                op.resultDescription = 'Disabled because license check failed';
                            }
                            return isEnabled;
                        });
                    }

                    return true;
                });
            } else {
                op.resultDescription = 'Disabled because not explicitly enabled';
                return false;
            }
        });
    }

    private registerHardcodedWorkflow(
        name: string,
        lambdas: ILambda[],
        inputSchemasToWorkflows = this.inputSchemasToWorkflows,
        options: IWorkflowRegistrationOptions
    ): Promise<void> {
        if (!name || name.length > 40) {
            throw new Error('Workflow name has invalid length');
        }
        if (new RegExp(/^[A-Z][a-zA-Z0-9_]+$/).test(name) === false) {
            throw new Error('Workflow name has invalid format');
        }
        inputSchemasToWorkflows.forEach(workflows => {
            if (workflows.some(workflow => workflow.name === name)) {
                throw new Error('Workflow name already registered.');
            }
        });

        const op = new LoggingOperation({
            operationName: 'WorkflowRegistration',
            resourceId: name,
            success: true
        });
        op.setClientMetadata(this.clientMetadata);
        op.start();

        return this.isWorkflowEnabled(name, lambdas, options.licenseFeatures, options.enabledByDefault, op).then((isEnabled: boolean): void => {
            op.resultSignature = isEnabled ? 'Enabled' : 'Disabled';
            op.stop();
            Log.info(0x222c3195 /* tag_8ldgv */, LogCategory.CoreDefault, op);

            if (isEnabled) {
                const inputSchema = lambdas[0].inputSchema;

                let workflows: Workflow[] = inputSchemasToWorkflows.get(inputSchema);
                if (!workflows) {
                    workflows = [];
                    inputSchemasToWorkflows.set(inputSchema, workflows);
                }

                let preExecutionPromise: Promise<void> = Promise.resolve();
                if (options.lookupTableLambda) {
                    preExecutionPromise = new Promise<void>((resolve, _reject) => {
                        const cv = new CorrelationVector();
                        const message: IClientMessage = {
                            messageType: MessageType.Input,
                            correlationVector: (cv as unknown) as ICorrelationVector,
                            payloadSchema: getTypeFromSchema(options.lookupTableLambda.inputSchema),
                            clientMetadata: this.clientMetadata,
                            payload: {}
                        };

                        const lambdaCallback = (message: IClientMessage): void => {
                            if (message.messageType === MessageType.Input && message.payload) {
                                this.lookupTableCache.set(name, message.payload);
                            }
                            resolve();
                        };

                        const nextStep = this.createLambdaExecutionStep(
                            '' /* workflowName */,
                            options.lookupTableLambda,
                            {} /* lambdaContext */,
                            lambdaCallback,
                            lambdaCallback
                        );
                        nextStep(message);
                    });
                }

                const workflow = new Workflow();
                workflow.name = name;
                workflow.lambdas = lambdas;
                workflow.canProduceNullResult = options.canProduceNullResult;
                workflow.shouldSendResultsToHost = options.shouldSendResultsToHost;
                workflow.triggerOnRefresh = options.triggerOnRefresh;
                workflow.throttleSettings = options.throttleSettings || this.defaultWorkflowOptions.throttleSettings;
                workflow.docIdExtractor = options.docIdExtractor;
                workflow.docSessionIdExtractor = (input: any) => {
                    if (input.isReduceInput) {
                        return input.id;
                    }
                    return getTileDocSessionId(inputSchema, input);
                };
                workflow.deferReduceUntilIdle = options.deferReduceUntilIdle;
                workflow.deferReduceUntilIdleMaxDelayMs = options.deferReduceUntilIdleMaxDelayMs;
                workflow.useCanaryTextTile = options.useCanaryTextTile;
                workflow.preExecutionPromise = preExecutionPromise;

                workflows.push(workflow);
            }
        });
    }

    // ExecuteLambda result signatures:
    // 'Exception' = lambda func resulted in exception
    // 'NoOutput'  = lambda func resulted in null output
    // 'Error'     = client runtime has an error
    private logOperation(op: LoggingOperation, success: boolean, resultSignature?: string, resultDescription?: string): void {
        op.stop();
        op.success = success;
        op.resultSignature = resultSignature;
        op.resultDescription = resultDescription;
        Log.info(0x222c3196 /* tag_8ldgw */, LogCategory.CoreDefault, op);
    }

    private handleLambdaError(exception: IException | Error, message: IClientMessage, op: LoggingOperation, exceptionType: ExceptionType, finish: (message: IClientMessage) => void): void {
        // Handle Exception outputs
        if ((exception as IException).exceptionType != undefined) {
            setMessageException(message, exception);
        } else if (exception instanceof Error) {
            setMessageException(message, {
                exceptionType: exceptionType,
                message: exception.message
            });
        } else {
            setMessageException(message, {
                exceptionType: ExceptionType.Unknown,
                message: exception ? exception.toString() : undefined
            });
        }

        this.logOperation(op, false, 'Exception', getMessageExceptionDescription(message));
        finish(message);
    }

    private handleLambdaOutput(output: any, message: IClientMessage, op: LoggingOperation, outputSchema: string, nextStep: (message: IClientMessage) => void, finish: (message: IClientMessage) => void): void {
        if (output && (output as IException).exceptionType !== ExceptionType.NoOutput) {
            if ((output as IException).exceptionType != undefined) {
                this.handleLambdaError(output, message, op, output.exceptionType, finish);
            } else if (output instanceof Error) {
                this.handleLambdaError(output, message, op, ExceptionType.LambdaErrorCallback, finish);
            } else {
                message.payload = output;
                message.payloadSchema = getTypeFromSchema(outputSchema);
                this.logOperation(op, true);
                nextStep(message);
            }
        } else {
            setMessageException(message, {
                exceptionType: ExceptionType.NoOutput
            });
            this.logOperation(op, true, 'NoOutput'); // Returning no output is not an error
            finish(message);
        }
    }

    private executeCustomLambdaStep(message: IClientMessage, op: LoggingOperation, customLambda: ICustomLambda, context: ILambdaContext, nextStep: (message: IClientMessage) => void, finish: (message: IClientMessage) => void): void {
        if (customLambda.func == null) {
            setMessageException(message, {
                exceptionType: ExceptionType.LambdaThrow,
                message: 'No lambda function set'
            });
            this.logOperation(op, false, 'Error', getMessageExceptionDescription(message));
            finish(message);
        } else {
            const done = (output: any): void => {
                this.handleLambdaOutput(output, message, op, customLambda.outputSchema, nextStep, finish);
            };

            const finishWorkflow = (output: any, outputSchema: string): void => {
                this.handleLambdaOutput(output, message, op, outputSchema, finish, finish);
            };

            try {
                customLambda.func(message.payload, customLambda.config, context, done, finishWorkflow);
            } catch (exception) {
                this.handleLambdaError(exception, message, op, ExceptionType.LambdaThrow, finish);
            }
        }
    }

    private executeLocalLambdaStep(message: IClientMessage, op: LoggingOperation, localLambda: ILocalLambda, nextStep: (message: IClientMessage) => void, finish: (message: IClientMessage) => void): void {
        if (this.hostCallbacks.executeLocalLambda == undefined) {
            setMessageException(message, {
                exceptionType: ExceptionType.LambdaThrow,
                message: 'No local lambda host callback set'
            });
            this.logOperation(op, false, 'Error', getMessageExceptionDescription(message));
            finish(message);
        } else {
            this.hostCallbacks.executeLocalLambda(localLambda.inputSchema, message.payload, localLambda.outputSchema)
                .then((output: any) => {
                    this.handleLambdaOutput(output, message, op, localLambda.outputSchema, nextStep, finish);
                })
                .catch(err => {
                    this.handleLambdaError(err, message, op, ExceptionType.LambdaThrow, finish);
                });
        }
    }

    private executeRemoteLambdaStep(message: IClientMessage, op: LoggingOperation, remoteLambda: IRemoteLambda, context: ILambdaContext, nextStep: (message: IClientMessage) => void, finish: (message: IClientMessage) => void): void {
        if (this.serviceUrl) {
            op.resourceId += `.${this.serviceProtocol}`;
            const done = (err, output) => {
                if (err) {
                    this.handleLambdaError(err, message, op, (err as IException).exceptionType || ExceptionType.LambdaErrorCallback, finish);
                } else {
                    this.handleLambdaOutput(output, message, op, remoteLambda.outputSchema, nextStep, finish);
                }
            };
            context.authTickets = remoteLambda.authTickets;
            if (remoteLambda.source === LambdaSource.Remote) {
                this.executeRemoteLambda(
                    this.serviceUrl,
                    message,
                    getTypeFromSchema(remoteLambda.inputSchema),
                    getTypeFromSchema(remoteLambda.outputSchema),
                    message.correlationVector,
                    this.clientMetadata,
                    this.hostCallbacks,
                    context,
                    done);
            } else { // LambdaSource.BatchedRemote
                if (!this.batchedRemoteLambdaManager) {
                    this.batchedRemoteLambdaManager = new BatchManager(
                        (input: any, batch: IBatch<any>, callback: (err?: any, output?: any) => void): void => {
                            const { remoteLambda, lambdaContext } = batch.context as IBatchedRemoteLambdaContext;
                            this.executeRemoteLambda(
                                this.serviceUrl,
                                {
                                    messageType: MessageType.Input,
                                    correlationVector: { id: batch.cv },
                                    payload: input,
                                    payloadSchema: getTypeFromSchema(remoteLambda.inputSchema),
                                    clientMetadata: this.clientMetadata
                                },
                                getTypeFromSchema(remoteLambda.remoteInputSchema),
                                getTypeFromSchema(remoteLambda.remoteOutputSchema),
                                { id: batch.cv },
                                this.clientMetadata,
                                this.hostCallbacks,
                                lambdaContext,
                                callback
                            );
                        },
                        (summarySchemaName: string, summary: any, context: IBatchContext): void => {
                            // Summary outputs are attached as doc-level entries in the result cache
                            const { lambdaContext } = context as IBatchedRemoteLambdaContext;
                            this.updateResultCacheForReduceWorkflows('docId'/*inputSchema*/, lambdaContext.docId, summarySchemaName, summary);
                        }
                    );
                }

                // Get the normalized batching config
                const { batchOptions } = remoteLambda as IBatchedRemoteLambda;
                const normalizedConfig: IBatchConfig<any> = this.normalizeBatchOptions(batchOptions);

                // Add the remote lambda name to the grouping key
                const { groupingKeyExtractor } = normalizedConfig;
                normalizedConfig.groupingKeyExtractor = input => `${remoteLambda.name}-${groupingKeyExtractor(input)}`;

                // Add the inputs to the next batch
                const batchedRemoteLambdaContext: IBatchedRemoteLambdaContext = {
                    name: remoteLambda.name,
                    remoteLambda: remoteLambda as IBatchedRemoteLambda,
                    lambdaContext: context
                };
                this.batchedRemoteLambdaManager.addBatchItem(
                    message.payload,
                    normalizedConfig,
                    batchedRemoteLambdaContext,
                    message.correlationVector.id.length > 127
                        ? message.correlationVector.id.substring(0, 127) + '!'
                        : message.correlationVector.id,
                    undefined,
                    done
                );
            }
        } else {
            setMessageException(message, {
                exceptionType: ExceptionType.LambdaThrow,
                message: 'No URL for remote lambda'
            });
            this.logOperation(op, false, 'Error', getMessageExceptionDescription(message));
            finish(message);
        }
    }

    private createLambdaExecutionStep(workflowName: string, lambda: ILambda, context: ILambdaContext, nextStep: (message: IClientMessage) => void, finish: (message: IClientMessage) => void): (message: IClientMessage) => void {
        return (message: IClientMessage): void => {
            const op = new LoggingOperation({
                cv: message.correlationVector.toString(),
                operationName: 'ExecuteLambda',
                resourceId: lambda.name,
                dimension0: workflowName
            });
            op.start();

            if (lambda.source === LambdaSource.Custom) {
                this.executeCustomLambdaStep(message, op, (lambda as ICustomLambda), context, nextStep, finish);
            } else if (lambda.source === LambdaSource.Local) {
                this.executeLocalLambdaStep(message, op, (lambda as ILocalLambda), nextStep, finish);
            } else if (lambda.source === LambdaSource.Remote || lambda.source === LambdaSource.BatchedRemote) {
                this.executeRemoteLambdaStep(message, op, (lambda as IRemoteLambda), context, nextStep, finish);
            } else {
                setMessageException(message, {
                    exceptionType: ExceptionType.LambdaThrow,
                    message: 'Unknown/No lambda location set'
                });
                this.logOperation(op, false, 'Error', getMessageExceptionDescription(message));
                finish(message);
            }
        };
    }

    private normalizeBatchOptions<T>(batchConfig: IBatchConfig<T>): IBatchConfig<T> {
        if (!batchConfig) {
            throw new Error('Expected batchConfig');
        }

        const newConfig: IBatchConfig<T> = { ...batchConfig };
        if (!batchConfig.estimateSize) {
            newConfig.estimateSize = estimateSize;
        }

        return newConfig;
    }
}

export const runtime: IClientRuntime = new ClientRuntime();
export const createRuntime = (): IClientRuntime => { return new ClientRuntime(); };
